import {
  Avatar,
  CalendarHeader,
  EmployeePicker,
  Loader,
  Pie,
  Show,
} from "components";
import {
  DocumentPlusIcon as DocumentPlusIconRaw,
  EnvelopeIcon as EnvelopeIconRaw,
  IdentificationIcon as IdentificationIconRaw,
  RectangleStackIcon as RectangleStackIconRaw,
  Square3Stack3DIcon as Square3Stack3DIconRaw,
  UsersIcon as UsersIconRaw,
} from "@heroicons/react/24/outline";
import { FC, useEffect, useMemo } from "react";
import { UserRole, useAppNavigate, useAppSearch, useCurrentUser } from "_graphql/cache/auth";
import { classNames, withRoles } from "utils";

import BarGraph from "components/graphs/bar";
import _ from "lodash";
import lodash from "lodash";
import moment from "moment";
import { motion } from "framer-motion";
import numeral from "numeral";
import { useFormik } from "formik";
import { useOverview } from "hooks/data/use-overview";

// Generic animated icon wrapper
const makeAnimatedIcon =
  (Raw: FC<any>) =>
    (props: any) => (
      <motion.span
        initial={{ opacity: 0, scale: 0.6, rotate: -10, y: 8 }}
        animate={{ opacity: 1, scale: 1, rotate: 0, y: 0 }}
        whileHover={{ scale: 1.12, rotate: 3 }}
        transition={{ type: "spring", stiffness: 260, damping: 20 }}
        className="inline-flex"
      >
        <Raw {...props} />
      </motion.span>
    );

// Animated Heroicons (names preserved for existing JSX usage)
const RectangleStackIcon = makeAnimatedIcon(RectangleStackIconRaw);
const DocumentPlusIcon = makeAnimatedIcon(DocumentPlusIconRaw);
const Square3Stack3DIcon = makeAnimatedIcon(Square3Stack3DIconRaw);
const UsersIcon = makeAnimatedIcon(UsersIconRaw);
const EnvelopeIcon = makeAnimatedIcon(EnvelopeIconRaw);
const IdentificationIcon = makeAnimatedIcon(IdentificationIconRaw);

// Optional animation helpers (can be used elsewhere in the file if desired)
export const fadeInUp = {
  hidden: { opacity: 0, y: 16 },
  show: (i: number = 0) => ({
    opacity: 1,
    y: 0,
    transition: { delay: 0.05 * i, duration: 0.4, ease: "easeOut" },
  }),
};

export const staggerContainer = {
  hidden: {},
  show: {
    transition: {
      staggerChildren: 0.07,
      delayChildren: 0.05,
    },
  },
};

// Dashboard statistics configuration (icons now animated)
const stats = [
  {
    id: 1,
    name: "Total Offers",
    accessor: "create-slip",
    icon: RectangleStackIcon,
    valueKey: "total_offers",
  },
  {
    id: 2,
    name: "Total Pending Offers",
    accessor: "create-slip",
    icon: DocumentPlusIcon,
    valueKey: "total_pending_offers",
  },
  {
    id: 3,
    name: "Total Closed Offers",
    accessor: "create-closing",
    valueKey: "total_closed_offers",
    icon: Square3Stack3DIcon,
  },
  {
    id: 4,
    name: "Total Open Offers",
    accessor: "create-closing",
    valueKey: "total_open_offers",
    icon: Square3Stack3DIcon,
  },
  {
    id: 5,
    name: "Total Claims",
    accessor: "create-closing",
    valueKey: "total_claim_offers",
    icon: Square3Stack3DIcon,
  },
  {
    id: 6,
    name: "Total Unclaimed Offers",
    accessor: "create-closing",
    valueKey: "total_unclaim_offers",
    icon: Square3Stack3DIcon,
  },
];

export {
  stats,
  // re-export animated icons if needed elsewhere
  RectangleStackIcon,
  DocumentPlusIcon,
  Square3Stack3DIcon,
  UsersIcon,
  EnvelopeIcon,
  IdentificationIcon,
};

interface clientsStatsType {
  id: number;
  name: string;
  icon: any;
  accessor: string;
  accessRoles?: UserRole[];
}

const clientsStats: clientsStatsType[] = [
  {
    id: 1,
    name: "Brokers",
    icon: UsersIcon,
    accessor: "total_brokers",
    accessRoles: [],
  },
  {
    id: 2,
    name: "Re-Insurers",
    icon: UsersIcon,
    accessor: "total_reinsurers",
    accessRoles: []
  },
  {
    id: 3,
    name: "Insurers",
    icon: UsersIcon,
    accessor: "total_insurers",
    accessRoles: []
  },
];

interface AmountValueType {
  title: string;
  value: string;
  display: "tab" | "pie" | "bar";
  accessRoles?: UserRole[];
}

const amountValues: AmountValueType[] = [
  {
    title: "Brokerage Earning",
    value: "brokerage_earnings",
    display: "tab",
    accessRoles: ["CEO", "Senior Broking Officer", "Finance Officer"],
  },
  {
    title: "Premium",
    value: "total_premium",
    display: "pie",
    accessRoles: []
  },
  {
    title: "Facultative Premium",
    value: "total_fac_premium",
    display: "pie",
    accessRoles: []
  },
  {
    title: "Sum Insured",
    value: "total_sum_insured",
    display: "pie",
    accessRoles: []
  },
  {
    title: "Facultative Sum Insured",
    value: "total_fac_sum_insured",
    display: "pie",
    accessRoles: []
  },
];

/**
 * Dashboard Page Component
 * Displays overview statistics and metrics for insurance operations
 */
const DashboardPage: FC = () => {
  const searchParams = useAppSearch();
  const navigate = useAppNavigate();
  const currentUser = useCurrentUser();

  const form = useFormik<any>({
    initialValues: {
      employee: "",
    },
    onSubmit: (values) => {
      navigate({
        search: (old) => ({
          ...old,
          ...values,
        }),
      });
    },
  });

  const filter = useMemo(
    () => ({
      from: searchParams.fromDate || moment().format("YYY-MM-DD"),
      to: searchParams.toDate || moment().format("YYY-MM-DD"),
      for: form.values.employee,
      filter_by: ["OPEN", "PENDING", "CLOSED"],
    }),
    [form.values.employee, searchParams.fromDate, searchParams.toDate]
  );

  const { overview, loading } = useOverview({
    dashboardInput: filter,
  });

  useEffect(() => {
    navigate({
      search: (old) => ({
        ...old,
        ...(!old?.fromDate
          ? {
            fromDate: searchParams?.fromDate || moment().format("YYYY-MM-DD"),
          }
          : {}),
        ...(!old?.toDate
          ? { toDate: searchParams?.toDate || moment().format("YYYY-MM-DD") }
          : {}),
        ...(!old?.view ? { view: "month" } : {}),
      }),
    });
  }, [navigate, searchParams?.fromDate, searchParams?.toDate]);


  return (
    <main className="flex-1 flex flex-col overflow-hidden h-screen bg-background-500">
      <CalendarHeader
        renderActions={() => (
          <>
            {withRoles(["System Administrator", "Managing Director", "CEO", "Senior Broking Officer"])(
              <EmployeePicker
                id={"employee"}
                placeholder="Select Employee"
                labelHidden
                rawId
                {...form}
              />
            )}
          </>
        )}
      />
      <Show if={loading}>
        <Loader />
      </Show>
      <Show if={!loading}>
        <div className="flex-1 p-2 sm:p-4 md:p-6 overflow-y-auto bg-background-500">
          <div className="grid grid-cols-1 items-start gap-4 lg:grid-cols-12 lg:gap-8">
            <div className="col-span-12">
              <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                <div className="relative isolate overflow-hidden rounded-lg bg-primary-400 px-3 py-3 sm:px-6 sm:pt-6 shadow row-span-1 col-span-1 sm:col-span-2">
                  <div className="absolute inset-y-0 right-8 -z-10 mr-16 w-[200%] origin-bottom-left skew-x-[-15deg] bg-primary-500 shadow-xl shadow-primary-400 ring-1 ring-primary-400 sm:mr-28 lg:mr-0 xl:mr-16 xl:origin-center" />
                  <div className="absolute inset-y-0 right-24 -z-10 mr-16 w-[200%] origin-bottom-left skew-x-[-20deg] bg-primary-600 shadow-xl shadow-primary-500 ring-1 ring-primary-500 sm:mr-28 lg:mr-0 xl:mr-16 xl:origin-center" />
                  <div className="absolute inset-0 -z-10 bg-gradient-to-b from-transparent via-primary-600/80 to-primary-600" />
                  <div>
                    {/* User Profile Section */}
                    <div className="flex flex-col sm:flex-row items-center gap-3 sm:gap-4">
                      <Avatar
                        alt={[
                          currentUser?.employee?.employee_first_name || "U",
                          currentUser?.employee?.employee_last_name || "A",
                        ].join(" ")}
                        size="md"
                        src={""}
                      />
                      <div className="flex-1 w-full">
                        <div className="flex items-center">
                          <h1 className="ml-0 sm:ml-3 text-white text-lg sm:text-xl font-extralight leading-7 sm:truncate sm:leading-9">
                            Welcome back,{" "}
                            <span className="font-semibold">
                              {currentUser?.employee?.employee_first_name}
                            </span>
                          </h1>
                        </div>
                        <dl className="mt-4 flex flex-col sm:ml-3 sm:mt-1 sm:flex-row sm:flex-wrap">
                          <dt className="sr-only">Company</dt>
                          <dd className="flex items-center text-xs sm:text-sm font-medium capitalize text-gray-50 sm:mr-6">
                            <EnvelopeIcon
                              className="mr-1.5 h-5 w-5 flex-shrink-0 text-primary-400"
                              aria-hidden="true"
                            />
                            {currentUser?.employee?.employee_email}
                          </dd>
                          <dt className="sr-only">Account status</dt>
                          <dd className="mt-2 sm:mt-0 flex items-center text-xs sm:text-sm font-medium capitalize text-gray-50 sm:mr-6">
                            <IdentificationIcon
                              className="mr-1.5 h-5 w-5 flex-shrink-0 text-primary-400"
                              aria-hidden="true"
                            />
                            {currentUser?.position}
                          </dd>
                        </dl>
                      </div>
                    </div>
                  </div>
                </div>
                {/* Statistics Cards */}
                {stats.map((item) => (
                  <div
                    key={item.id}
                    className="relative overflow-hidden rounded-lg bg-card-500 px-3 py-4 sm:px-6 sm:pt-6 shadow"
                  >
                    <dt>
                      <div className="absolute rounded-md bg-primary-500 p-2 sm:p-3">
                        <item.icon
                          className="h-5 w-5 sm:h-6 sm:w-6 text-card-400"
                          aria-hidden="true"
                        />
                      </div>
                      <p className="ml-12 sm:ml-16 truncate text-xs sm:text-sm font-medium text-tertiary">
                        {item.name}
                      </p>
                    </dt>
                    <dd className="ml-12 sm:ml-16 flex justify-between items-end pb-4 sm:pb-7">
                      <p className="text-xl sm:text-2xl font-semibold text-primary">
                        {numeral(lodash.get(overview, item.valueKey, 0)).format(
                          "0,0"
                        )}
                      </p>
                    </dd>
                  </div>
                ))}
              </dl>
            </div>
          </div>

          {/* Clients Overview Section */}
          <div className="mt-6">
            <h3 className="text-base font-semibold leading-6 text-primary">
              Clients Overview
            </h3>

            <dl className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {clientsStats.map((item) => withRoles(item?.accessRoles || [])(
                <div
                  key={item.id}
                  className="flex items-center space-x-4 sm:space-x-6 overflow-hidden rounded-lg bg-card-500 p-3 sm:p-6 shadow"
                >
                  <div className="rounded-md bg-primary-500 flex items-center justify-center aspect-square h-12 w-12 sm:h-14 sm:w-14">
                    <item.icon
                      className="h-5 w-5 sm:h-6 sm:w-6 text-card-400"
                      aria-hidden="true"
                    />
                  </div>
                  <div className="flex-1">
                    <dt>
                      <p className="truncate text-xs sm:text-sm font-medium text-tertiary">
                        {item.name}
                      </p>
                    </dt>
                    <dd className="mt-1 flex items-baseline justify-between md:block lg:flex">
                      <div className="flex items-baseline text-xl sm:text-2xl font-semibold text-primary">
                        {numeral(lodash.get(overview, item.accessor, 0)).format(
                          "0,0"
                        )}
                      </div>
                    </dd>
                  </div>
                </div>
              ))}
            </dl>
          </div>

          {/* Financial Metrics Section */}
          <div className="mt-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5">
              {amountValues.map(({ title, value, display, accessRoles }) =>
                withRoles(accessRoles || [])(
                  <>
                    {display === "pie" ? (
                      <div className="flex flex-col">
                        <div className="flex justify-between">
                          <h3 className="text-base sm:text-lg font-semibold leading-6 text-primary mb-2 sm:mb-4">
                            {title}
                          </h3>
                          <div>

                          </div>
                        </div>
                        <div className="bg-card-500 backdrop-blur-sm rounded-xl shadow-lg p-3 sm:p-6">
                          <div className="flex flex-col md:flex-row md:space-x-8">
                            <div className="flex-1 space-y-3 sm:space-y-4">
                              {[
                                {
                                  color: "#2563EB",
                                  bgColor: "bg-blue-600",
                                  id: "Ghanaian Cedi",
                                  symbol: "₵",
                                  label: "Ghanaian Cedi",
                                  value: numeral(lodash.get(overview, `${value}.total_cedis`, 0)).value() || 0,
                                },
                                {
                                  color: "#0d9488",
                                  bgColor: "bg-teal-600",
                                  id: "US Dollar",
                                  symbol: "$",
                                  label: "US Dollar",
                                  value: numeral(lodash.get(overview, `${value}.total_dollar`, 0)).value() || 0,
                                },
                                {
                                  color: "#9333ea",
                                  bgColor: "bg-purple-600",
                                  id: "Euro",
                                  symbol: "€",
                                  label: "Euro",
                                  value: numeral(lodash.get(overview, `${value}.total_euros`, 0)).value() || 0,
                                },
                                {
                                  color: "#CA8A04",
                                  bgColor: "bg-yellow-600",
                                  id: "British Pound",
                                  symbol: "£",
                                  label: "British Pound",
                                  value: numeral(lodash.get(overview, `${value}.total_pounds`, 0)).value() || 0,
                                },
                              ]?.map?.((item: any) => (
                                <div
                                  key={item?.id}
                                  className="bg-card-700/50 rounded-lg p-2 sm:p-3 flex items-center space-x-3 sm:space-x-4 hover:bg-card-700/70 transition-colors duration-200"
                                >
                                  <div
                                    className={classNames(
                                      item?.bgColor,
                                      "rounded-lg p-2 sm:p-3 shadow-lg"
                                    )}
                                  >
                                    <span className="text-base sm:text-lg text-white font-medium">
                                      {item?.symbol}
                                    </span>
                                  </div>
                                  <div className="flex-1">
                                    <dt className="text-xs sm:text-sm font-medium text-tertiary">
                                      {item?.label}
                                    </dt>
                                    <dd className="mt-1">
                                      <p className="text-lg sm:text-xl font-semibold text-primary">
                                        {numeral(item?.value).format("0,0.00")}
                                      </p>
                                    </dd>
                                  </div>
                                </div>
                              ))}
                            </div>
                            <div className="w-full h-56 sm:w-80 sm:h-80 mt-4 md:mt-0">
                              <Pie
                                innerRadius={0.2}
                                key={title}
                                data={[
                                  {
                                    color: "#2563EB",
                                    bgColor: "bg-blue-600",
                                    id: "Ghanaian Cedi",
                                    label: "Ghanaian Cedi",
                                    value: numeral(lodash.get(overview, `${value}.total_cedis`, 0)).value() || 0,
                                  },
                                  {
                                    color: "#0d9488",
                                    bgColor: "bg-teal-600",
                                    id: "US Dollar",
                                    label: "US Dollar",
                                    value: numeral(lodash.get(overview, `${value}.total_dollar`, 0)).value() || 0,
                                  },
                                  {
                                    color: "#9333ea",
                                    bgColor: "bg-purple-600",
                                    id: "Euro",
                                    label: "Euro",
                                    value: numeral(lodash.get(overview, `${value}.total_euros`, 0)).value() || 0,
                                  },
                                  {
                                    color: "#CA8A04",
                                    bgColor: "bg-yellow-600",
                                    id: "British Pound",
                                    label: "British Pound",
                                    value: numeral(lodash.get(overview, `${value}.total_pounds`, 0)).value() || 0,
                                  },
                                ]}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    ) : display === "bar" ? (
                      <div className="flex flex-col">
                        <div className="flex justify-between">
                          <h3 className="text-base sm:text-lg font-semibold leading-6 text-primary mb-2 sm:mb-4">
                            {title}
                          </h3>
                          <div>

                          </div>
                        </div>
                        <div className="bg-card-500  backdrop-blur-sm rounded-xl shadow-lg p-3 sm:p-6">
                          <div className="flex h-56 flex-col md:flex-row md:space-x-8">

                            <BarGraph
                              fromDate="2022-01-01"
                              toDate="2022-12-31"
                              indexBy="label"
                              key={title}
                              keys={[
                                {
                                  accessor: "applicationFeeGenerated",
                                  label: "Application Fee Generated",
                                  color: "#d97706",
                                  bgColor: "bg-amber-600",
                                }
                              ]}
                              data={[
                                {
                                  color: "#2563EB",
                                  bgColor: "bg-blue-600",
                                  id: "Ghanaian Cedi",
                                  label: "Ghanaian Cedi",
                                  value: numeral(lodash.get(overview, `${value}.total_cedis`, 0)).value() || 0,
                                },
                                {
                                  color: "#0d9488",
                                  bgColor: "bg-teal-600",
                                  id: "US Dollar",
                                  label: "US Dollar",
                                  value: numeral(lodash.get(overview, `${value}.total_dollar`, 0)).value() || 0,
                                },
                                {
                                  color: "#9333ea",
                                  bgColor: "bg-purple-600",
                                  id: "Euro",
                                  label: "Euro",
                                  value: numeral(lodash.get(overview, `${value}.total_euros`, 0)).value() || 0,
                                },
                                {
                                  color: "#CA8A04",
                                  bgColor: "bg-yellow-600",
                                  id: "British Pound",
                                  label: "British Pound",
                                  value: numeral(lodash.get(overview, `${value}.total_pounds`, 0)).value() || 0,
                                },
                              ]}
                            />
                          </div>
                        </div>
                      </div>

                    ) : (
                      <div className="space-y-3 sm:space-y-4 col-span-1 sm:col-span-2">
                        <h3 className="text-base sm:text-lg font-semibold text-primary">
                          {title}
                        </h3>
                        <div className="bg-card-600/50 backdrop-blur-sm rounded-xl p-3 sm:p-6 shadow-lg">
                          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-6">
                            <div className="flex flex-col space-y-1 sm:space-y-2">
                              <span className="text-xs sm:text-sm font-medium text-gray-400">Total GHS</span>
                              <span className="text-lg sm:text-2xl font-bold text-primary">
                                {numeral(lodash.get(overview, `${value}.total_cedis`, 0)).format("0,0.00")}
                              </span>
                            </div>
                            <div className="flex flex-col space-y-1 sm:space-y-2">
                              <span className="text-xs sm:text-sm font-medium text-gray-400">Total USD</span>
                              <span className="text-lg sm:text-2xl font-bold text-primary">
                                {numeral(lodash.get(overview, `${value}.total_dollar`, 0)).format("0,0.00")}
                              </span>
                            </div>
                            <div className="flex flex-col space-y-1 sm:space-y-2">
                              <span className="text-xs sm:text-sm font-medium text-gray-400">Total EUR</span>
                              <span className="text-lg sm:text-2xl font-bold text-primary">
                                {numeral(lodash.get(overview, `${value}.total_euros`, 0)).format("0,0.00")}
                              </span>
                            </div>
                            <div className="flex flex-col space-y-1 sm:space-y-2">
                              <span className="text-xs sm:text-sm font-medium text-gray-400">Total GBP</span>
                              <span className="text-lg sm:text-2xl font-bold text-primary">
                                {numeral(lodash.get(overview, `${value}.total_pounds`, 0)).format("0,0.00")}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </>
                )
              )}
            </div>
          </div>
        </div>
      </Show>
    </main>
  );
};

export default DashboardPage;
